/**
 * Dashboard domains translations: domains, domain (detail features like DynDNS)
 */

export const domains = {
  overview: {
    title: 'Domains',
    subtitle: 'Manage your registered domains',
    loading: 'Loading domains...',
    noDomains: 'No domains available',
    noDomainsText: 'You have not registered any domains yet. Register a new domain to get started.',
    registerDomain: 'Register domain',
    contacts: 'Contacts',
    search: 'Search domain...',
    sort: 'Sort:',
    sortName: 'Name',
    sortExpiry: 'Expiry date',
    sortRegistered: 'Registered',
    allDomains: 'All domains',
    activeDomains: 'Active domains',
    expiringDomains: 'Expiring soon',
    canceledDomains: 'Cancelled domains',
    suspendedDomains: 'Suspended domains',
    domain: 'Domain',
    domains: 'Domains',
    noResults: 'No domains found for "{query}"',
    noResultsCategory: 'No domains in this category',
    resetFilters: 'Reset filters',
    daysRemaining: '{days} days',
    externalBadge: 'Email',
    emailHosting: 'Email Hosting',
    dnsSettings: 'DNS settings',
    manageEmail: 'Manage email',
    orderEmail: 'Order email hosting',
    settings: 'Settings',
    manage: 'Manage',
    subCustomer: 'Sub-customer',
    filterAll: 'All',
    filterOwn: 'Own',
    filterSubCustomer: 'Sub-customers',
    switchToManage: 'Switch & manage',
    statsBreakdown: '{own} own + {sub} sub-customers',
    sortOwner: 'Owner',
    stats: {
      total: 'Total',
      active: 'Active',
      expiring: 'Expiring',
      canceled: 'Cancelled',
      suspended: 'Suspended'
    },
    status: {
      active: 'Active',
      canceled: 'Cancelled',
      suspended: 'Suspended',
      deleted: 'Deleted',
      tradeOut: 'Transferred away',
      transferPending: 'Transfer pending',
      expiring: 'Expiring soon',
      unknown: 'Unknown'
    }
  },
  management: {
    title: 'Domain Management',
    errorLoading: 'Error loading',
    errorMessage: 'Domain details could not be loaded. Please try again later.',
    retry: 'Retry',
    suspended: {
      banner: 'This domain is currently suspended',
      reason: 'Reason: {reason}',
      since: 'Suspended since: {date}',
      contactSupport: 'Please contact support for more information.',
      reasons: {
        abuse: 'Abuse',
        nis2_violation: 'NIS2 violation',
        legal: 'Legal reasons',
        manual: 'Manually suspended',
        unknown: 'Not specified'
      }
    },
    externalDomain: {
      subtitle: 'Email Domain',
      info: 'This domain is externally managed and used at ITSH for email hosting.',
      manageEmail: 'Manage email',
      manageEmailDesc: 'Manage mailboxes, forwarding, and DNS settings for this domain.'
    },
    transferPending: {
      title: 'Transfer in progress',
      since: 'Started on {date}.',
      message: 'You can already prepare DNS records and DynDNS. Name servers, contacts and the other domain settings become available once the transfer has completed.'
    }
  },
  contract: {
    title: 'Contract Details',
    subtitle: 'Contract information and duration',
    startDate: 'Contract start',
    nextRenewal: 'Next renewal',
    expiresOn: 'Expires on',
    canceledOn: 'Cancelled on',
    cancellationPeriod: 'Cancellation period:',
    contractDuration: 'Contract duration:',
    billingPeriod: 'Billing period:',
    days30: '30 days',
    month1: '1 month',
    months3: '3 months',
    months6: '6 months',
    months12: '12 months',
    inDays: 'in {days} days'
  },
  actions: {
    title: 'Domain Actions',
    subtitle: 'Management and settings',
    transfer: 'Transfer',
    transferDesc: 'Transfer domain to another provider',
    cancel: 'Cancel',
    cancelDesc: 'Cancel domain contract at the next renewal date',
    cancelRevoke: 'Revoke cancellation',
    cancelRevokeDesc: 'Withdraw existing cancellation',
    manageEmail: 'Manage email',
    manageEmailDesc: 'Mailboxes, forwards and DNS for this domain',
    orderEmail: 'Order email hosting',
    orderEmailDesc: 'Set up mailboxes for this domain',
    linkPackage: 'Web package',
    linkPackageDesc: 'Link domain with a web package',
    comingSoon: 'Soon',
    transferLock: 'Transfer lock',
    transferLocked: 'Domain is locked',
    transferUnlocked: 'Transfers allowed',
    transferLockHintLocked: 'Disable the lock to transfer the domain to another provider.',
    transferLockHintUnlocked: 'Enable the lock to prevent unwanted transfers.',
    transferLockUpdated: 'Transfer lock updated',
    transferLockEnabled: 'The domain is now locked against transfers.',
    transferLockDisabled: 'The transfer lock has been lifted. The domain can now be transferred.',
    whoisProtection: 'WHOIS protection',
    whoisProtectionEnabled: 'WHOIS data is protected',
    whoisProtectionDisabled: 'WHOIS data is public',
    whoisProtectionUpdated: 'WHOIS protection updated',
    whoisProtectionHintEnabled: 'Your personal data is hidden from public WHOIS queries. Billing occurs annually at the next renewal.',
    whoisProtectionHintDisabled: 'Your personal data is visible in public WHOIS queries.',
    trusteeService: 'Trustee service',
    trusteeServiceEnabled: 'Trustee service is active',
    trusteeServiceDisabled: 'Trustee service is inactive',
    trusteeServiceUpdated: 'Trustee service updated',
    trusteeServiceHintEnabled: 'A local trustee serves as the admin contact for this domain. Billing occurs annually at the next renewal.',
    trusteeServiceHintDisabled: 'No trustee active. Some TLDs require an admin contact residing in the target country.',
    addonPricePerYear: '{price}/year incl. VAT',
    addonPricePerYearNet: '{price}/year, no VAT',
    addonNotSupported: '{addon} is not available for .{tld} domains.',
    addonConfirmTitle: 'Add paid service?',
    addonConfirmText:
      '{addon} will be added to your contract for {price} per year (incl. VAT). Billing occurs at the next renewal.',
    addonConfirmTextNet:
      '{addon} will be added to your contract for {price} per year (no VAT charged). Billing occurs at the next renewal.',
    addonConfirmTextNoPrice:
      '{addon} will be added to your contract as a paid service. Billing occurs at the next renewal.',
    addonConfirmAction: 'Add and pay'
  },
  transfer: {
    title: 'Domain Transfer',
    authCodeTitle: 'AuthCode',
    subtitle: 'Transfer domain to another provider',
    authCodeSubtitle: 'Use this code with your new provider',
    warning: 'Warning:',
    warningText: 'After the transfer, the domain will no longer be managed through this portal.',
    step1: 'Request AuthCode here',
    step2: 'Set up domain with the new provider',
    step3: 'Enter AuthCode there',
    validUntil: 'Valid until',
    infoText: 'Copy this code and enter it at your new provider to start the transfer.',
    close: 'Close',
    cancel: 'Cancel',
    requestAuthCode: 'Request AuthCode',
    loading: 'Loading...',
    copied: 'Copied',
    copiedMessage: 'AuthCode has been copied to clipboard'
  },
  cancel: {
    title: 'Cancel domain',
    confirmTitle: 'Confirm cancellation',
    question: 'When should the cancellation take effect?',
    endOfPeriod: 'At the end of the registration period',
    endOfPeriodDesc: 'Recommended. The domain remains active until then and can be transferred to another provider by AuthCode at any point before the period ends.',
    customDate: 'Custom date',
    earlyForfeitWarning: 'Choosing a date before the end of the registration period forfeits the fee already paid to the registry. No refund is possible, because we acquired the period from the registry irrevocably.',
    warning: 'Warning!',
    warningText: 'The domain will be deleted on the date shown and is then released for registration again.',
    revocableNote: 'Until then you can withdraw the cancellation at any time via "Revoke cancellation".',
    domainLabel: 'Domain',
    cancellationDate: 'Cancellation date',
    confirmText: 'Do you really want to cancel {domain} {date}?',
    toDate: 'on {date}',
    back: 'Back',
    cancel: 'Cancel',
    continue: 'Continue',
    confirmCancel: 'Cancel domain',
    success: 'Cancellation successful',
    successMessage: '{domain} has been cancelled'
  },
  cancelRevoke: {
    title: 'Revoke cancellation',
    subtitle: 'Withdraw domain cancellation',
    question: 'Do you really want to revoke the cancellation of this domain?',
    info: 'After revocation, the domain will continue to be renewed and billed as usual.',
    cancel: 'Cancel',
    confirm: 'Revoke cancellation',
    processing: 'Processing...',
    success: 'Success',
    successMessage: 'Your cancellation has been successfully revoked!',
    error: 'Error',
    checkingPrice: 'Checking price...',
    redemptionQuestion: 'This domain has already expired. It can only be recovered from the registry for a fee. Do you want to do that?',
    redemptionFee: 'Redemption fee',
    redemptionInfo: 'The amount is charged immediately to your saved payment method. If the registry restore fails, we refund it automatically.',
    priceUnavailable: 'No price is available for this domain right now. Please contact support.',
    confirmPaid: 'Recover and pay'
  },
  packageLink: {
    title: 'Link web package',
    subtitle: 'Connect domain with a hosting package',
    currentlyLinked: 'Currently linked to:',
    selectPackage: 'Select web package',
    selectPlaceholder: 'Choose web package...',
    noPackages: 'No web packages available',
    info: 'Linking enables the use of subdomains and additional features.',
    unlink: 'Unlink',
    confirmUnlink: 'Really unlink',
    error: 'Linking failed',
    notLinkable: 'The domain cannot be linked to this package. Pick a running web package of your account.',
    unlinkMailWarning: 'If you unlink the domain, email for it is switched off within the hour unless email hosting is booked for it. Mailboxes and emails are retained.',
    cancel: 'Cancel',
    save: 'Save',
    success: 'Success',
    successMessage: 'Web package has been successfully linked!'
  },
  contacts: {
    title: 'Contacts',
    manage: 'Manage',
    loading: 'Loading...',
    owner: 'Owner (Owner-C)',
    admin: 'Administrator (Admin-C)',
    clickToAssign: 'Click to assign',
    notSupported: 'Not supported by this registry',
    selectContact: 'Select contact',
    contactUpdated: 'Contact updated',
    ownerChanged: 'Owner has been changed.',
    adminChanged: 'Administrator has been changed.',
    registrantTradeRequired: 'Changing the registrant for this domain incurs additional charges. Please contact support.',
    verification: {
      verified: 'Verified',
      unverified: 'Not verified',
      emailVerified: 'Email verified',
      emailUnverified: 'Email not verified',
      phoneVerified: 'Phone verified',
      phoneUnverified: 'Phone not verified',
      email: {
        verify: 'Verify email',
        requestTitle: 'Verify email',
        requestDescription: 'We will send a verification code to {email}. Click "Send code" to continue.',
        title: 'Confirm email address',
        description: 'We have sent a verification code to {email}.',
        success: 'Email successfully verified',
        autoVerified: 'Email was automatically verified',
        alreadyVerified: 'This email address was already verified'
      },
      phone: {
        verify: 'Verify phone',
        requestTitle: 'Verify phone',
        requestDescription: 'We will send a verification code via SMS to {phone}. Click "Send code" to continue.',
        title: 'Confirm phone number',
        description: 'We have sent a verification code via SMS to {phone}.',
        success: 'Phone number successfully verified',
        alreadyVerified: 'This phone number was already verified',
        wrongNumber: 'Wrong number? Edit contact',
        wrongNumberAccount: 'Wrong number? Change in account settings'
      },
      otpPlaceholder: 'Enter verification code',
      sendCode: 'Send code',
      confirm: 'Confirm',
      cancel: 'Cancel',
      errors: {
        invalidOtp: 'Invalid verification code. After five wrong codes, request a new one.',
        expired: 'Code expired. Please request a new one.',
        badRequest: 'Invalid request',
        handleNotFound: 'Contact not found',
        serviceUnavailable: 'The verification service is currently unavailable',
        tooManyRequests: 'Too many codes requested for this contact. Please try again in an hour.',
        phoneNotMobile: 'Codes can only be sent by SMS to mobile numbers. A number outside the EEA, Switzerland and the UK has to be in your own country.',
        smsUnavailable: 'Verification by SMS is not available right now. Please try again tomorrow.',
        tooManyFailures: 'Too many wrong codes for this contact. Please try again tomorrow.',
        smsDailyLimit: 'Your account has used today\'s verification texts. Please try again tomorrow.',
        contactChanged: 'This contact changed while it was being verified, or your account details are still being saved to it. Reload the page or save your account details again, then retry.',
        generic: 'An error occurred. Please try again.'
      },
      needsRevalidation: 'Verification expired - please confirm again',
      whyRequired: 'According to the NIS2 regulation, contact details for domains must be verified to ensure the domain holder can be reached.'
    },
    nis2Warning: {
      title: 'NIS2 Notice',
      message: 'Organisation data will be publicly visible in WHOIS (RDDS) in accordance with NIS2 regulation.',
      emailTip: "Tip: Use functional email addresses (info{'@'}, admin{'@'}, domain{'@'}) instead of personal emails for organisation contacts."
    },
    deadline: {
      warning: 'Verification expiring',
      critical: 'Verification expiring soon',
      expired: 'Verification expired',
      daysRemaining: '{days} days remaining',
      expiredMessage: 'The verification deadline has passed. Please update and verify your contact details.'
    }
  },
  handlesOverview: {
    title: 'Contacts',
    subtitle: 'Manage your domain contacts (handles)',
    createContact: 'Create contact',
    total: 'Total',
    persons: 'People',
    organizations: 'Organizations',
    accountHandles: 'Account Handles',
    searchPlaceholder: 'Search contacts...',
    loading: 'Loading contacts...',
    empty: {
      title: 'No contacts',
      description: 'You haven\'t created any domain contacts yet. Create a new contact to use it for domain registrations.'
    },
    allContacts: 'All contacts',
    contactCount: '{count} contact | {count} contacts',
    noResults: {
      search: 'No contacts found for "{query}"',
      category: 'No contacts in this category',
      resetFilter: 'Reset filter'
    }
  },
  nameservers: {
    title: 'Nameservers',
    subtitle: 'Configure DNS resolution',
    current: 'Current nameservers',
    external: 'External',
    useInwx: 'Use ITSH nameservers',
    useExternal: 'Use external nameservers',
    inwxDescription: 'DNS management through this control panel',
    externalDescription: 'Use your own external nameservers',
    placeholder: 'ns1.example.com',
    add: 'Add nameserver',
    remove: 'Remove',
    save: 'Save',
    saving: 'Saving...',
    minRequired: 'At least 2 nameservers required',
    invalidHostname: 'Invalid hostname',
    confirmSwitch: 'Change nameservers?',
    confirmSwitchMessage: 'Changing nameservers can take up to 48 hours for changes to propagate worldwide. When switching to external nameservers, DNS records in this panel will no longer be used.',
    updated: 'Nameservers updated',
    switchedToInwx: 'The domain now uses ITSH nameservers.',
    switchedToExternal: 'The domain now uses external nameservers.',
    updateError: 'Error updating'
  },
  dns: {
    title: 'DNS Records',
    subtitle: 'Manage DNS records for {domain}',
    add: 'Add record',
    import: 'Import',
    templates: 'Templates',
    bulkActions: 'Bulk actions',
    loading: 'Loading DNS records...',
    noRecords: 'No DNS records available',
    noRecordsText: 'Click "Add DNS record" to create a new record.',
    noFilterResults: 'No matches found',
    noFilterResultsText: 'No DNS records match your filter criteria.',
    resetFilter: 'Reset filter',
    type: 'Type',
    name: 'Name',
    value: 'Value',
    ttl: 'TTL',
    priority: 'Priority',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    protectedRecord: 'This record is managed by the provider',
    selectAll: 'Select all',
    addRecord: 'Add DNS record',
    editRecord: 'Edit DNS record',
    recordType: 'Record type',
    recordName: 'Name',
    recordValue: 'Value',
    recordTtl: 'TTL (seconds)',
    recordPriority: 'Priority',
    namePlaceholder: "e.g. www or {'@'} for root",
    valuePlaceholder: 'e.g. 192.0.2.1 or example.com',
    ttlPlaceholder: 'Default: 3600',
    priorityPlaceholder: 'Only for MX/SRV',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    recordAdded: 'DNS record added',
    recordUpdated: 'DNS record updated',
    deleteTitle: 'Delete DNS record',
    deleteQuestion: 'Do you really want to delete this DNS record?',
    deleteWarning: 'This action cannot be undone.',
    deleteConfirm: 'Delete',
    deleting: 'Deleting...',
    recordDeleted: 'DNS record deleted',
    conflictDyndnsManaged:
      'This record is managed by DynDNS and cannot be modified manually.',
    conflictRecordExists:
      'A DNS record with this name, type, and content already exists.',
    externalNsWarningTitle: 'External nameservers active',
    externalNsWarningText: 'DNS records cannot be edited because external nameservers are being used. Manage the DNS records with your external nameserver provider.',
    recordDeletedMessage: 'The DNS record has been successfully deleted.',
    importTitle: 'Import DNS records',
    importSubtitle: 'Import DNS records from a zone file',
    importPlaceholder: 'Paste your zone file here...',
    importButton: 'Import',
    importing: 'Importing...',
    importSuccess: 'DNS records imported',
    templatesTitle: 'DNS Templates',
    templatesSubtitle: 'Select a template',
    templatesApply: 'Apply',
    bulkSelectAll: 'Select all',
    bulkDeselectAll: 'Deselect all',
    bulkDelete: 'Delete selected',
    bulkChangeTtl: 'Change TTL',
    bulkSelected: '{count} selected',
    bulkDeleteConfirm: 'Delete {count} DNS records?',
    bulkTtlLabel: 'TTL for {count} records:',
    bulkClearSelection: 'Clear selection',
    bulkApply: 'Apply',
    bulkDeletedTitle: 'DNS records deleted',
    bulkDeletedSuccess: '{count} records deleted.',
    bulkDeletedPartial: '{success} records deleted, {fail} failed.',
    bulkTtlUpdatedTitle: 'TTL updated',
    bulkTtlUpdatedSuccess: 'TTL set to {ttl} for {count} records.',
    bulkTtlUpdatedPartial: '{success} records updated, {fail} failed.',
    bulkCreateTitle: 'Create multiple DNS records',
    bulkCreateSubtitle: 'Add multiple records at once',
    bulkCreatePlaceholder: 'One record per line in format: TYPE NAME VALUE TTL PRIORITY',
    bulkCreate: 'Create',
    bulkCreating: 'Creating...',
    bulkCreateSuccess: 'Records created',
    bulk: {
      title: 'Create multiple DNS records',
      addRow: 'Add record',
      createCount: 'Create {count} records',
      creating: 'Creating...',
      progress: '{current}/{total} created',
      cancel: 'Cancel',
      colType: 'Type',
      colName: 'Name',
      colContent: 'Content',
      colTtl: 'TTL',
      colPriority: 'Prio',
      nameHint: "Use {'@'} as placeholder for the domain",
      contentPlaceholder: 'Content...',
      contentRequired: 'Content is required',
      invalidIpv4: 'Invalid IPv4 address',
      invalidIpv6: 'Invalid IPv6 address',
      priorityRequired: 'Priority is required',
      completeTitle: 'Creation complete',
      completeSuccess: '{count} DNS records successfully created.',
      completePartial: '{success} records created, {failed} failed.'
    },
    importModal: {
      title: 'Import zone file',
      dropText: 'Drag zone file here',
      dropHint: 'or click to select',
      supportedFormats: 'Supported formats: BIND zone files (.zone, .txt)',
      parsingFailed: 'Parsing failed',
      parsingFailedMessage: 'The zone file could not be read.',
      noRecords: 'No records',
      noRecordsMessage: 'The zone file contains no valid DNS records.',
      fileError: 'The file could not be read.',
      parseWarnings: '{count} warnings during parsing',
      andMore: '...and {count} more',
      recordsToImport: '{count} DNS records to import',
      colName: 'Name',
      colType: 'Type',
      colContent: 'Content',
      colTtl: 'TTL',
      moreRecords: '...and {count} more records',
      importedProgress: '{current}/{total} imported',
      importComplete: 'Import complete',
      importCompleteSuccess: '{count} DNS records successfully imported.',
      importCompletePartial: '{success} records imported, {fail} failed.',
      importButton: 'Import {count} records',
      importing: 'Importing...'
    },
    templateModal: {
      modalTitle: 'DNS Templates',
      predefinedSection: 'Predefined templates',
      recordsCount: '{count} records',
      predefined: 'Predefined',
      recordsPreviewTitle: 'DNS records in this template:',
      colName: 'Name',
      colType: 'Type',
      colContent: 'Content',
      colTtl: 'TTL',
      colPriority: 'Priority',
      emptyContent: '(to be set)',
      progressCreated: '{current}/{total} created',
      noteAddedToExisting: 'The records will be added to your existing DNS configuration.',
      close: 'Close',
      cancel: 'Cancel',
      applyButton: 'Apply template',
      applyingButton: 'Applying...',
      appliedTitle: 'Template applied',
      appliedSuccessMessage: '{count} DNS records created from template.',
      appliedPartialMessage: '{success} records created, {fail} failed.',
      builtinBasicWebsite: {
        name: 'Basic Website',
        description: 'A and CNAME records for a simple website'
      },
      builtinGoogleWorkspace: {
        name: 'Google Workspace',
        description: 'MX and TXT records for Google Workspace email'
      },
      builtinMicrosoft365: {
        name: 'Microsoft 365',
        description: 'MX and TXT records for Microsoft 365 email'
      },
      builtinMailserver: {
        name: 'Custom Mail Server',
        description: 'MX, SPF and DMARC for your own mail server'
      },
      builtinSubdomainRedirect: {
        name: 'Subdomain Redirect',
        description: 'CNAME records for common subdomains'
      }
    },
    ttlPresets: {
      fiveMinutes: '5 minutes',
      fifteenMinutes: '15 minutes',
      thirtyMinutes: '30 minutes',
      oneHour: '1 hour',
      fourHours: '4 hours',
      twentyFourHours: '24 hours',
      seconds: '{value} sec',
      minutes: '{value} min',
      hours: '{value} hrs',
      days: '{value} days'
    },
    recordTypes: {
      A: { description: 'IPv4 address', hint: 'IPv4 address of the target' },
      AAAA: { description: 'IPv6 address', hint: 'IPv6 address of the target' },
      CNAME: { description: 'Alias to hostname', hint: 'Target hostname (with trailing dot)' },
      TXT: { description: 'Text information', hint: 'Text value (e.g. SPF, DKIM, verification)' },
      ALIAS: { description: 'Alias to root', hint: 'Target domain for root alias' },
      PTR: { description: 'Reverse DNS', hint: 'Hostname for reverse lookup' },
      NS: { description: 'Nameserver', disabledReason: 'Managed by the nameserver' },
      SOA: { description: 'Start of Authority', disabledReason: 'Managed by the nameserver' },
      MX: { description: 'Mail server', hint: 'Mail server hostname' },
      RP: { description: 'Responsible Person', hint: 'Responsible person (mbox txt)' },
      CAA: { description: 'SSL authorisation', hint: 'Flags Tag Value (e.g. 0 issue "ca.com")' },
      TLSA: { description: 'DANE/TLS certificate', hint: 'Usage Selector MatchingType CertData' },
      SSHFP: { description: 'SSH fingerprint', hint: 'Algorithm Type Fingerprint' },
      SMIMEA: { description: 'S/MIME certificate', hint: 'Usage Selector MatchingType CertData' },
      OPENPGPKEY: { description: 'OpenPGP key', hint: 'Base64-encoded OpenPGP key' },
      CERT: { description: 'Certificate', hint: 'Type KeyTag Algorithm Certificate' },
      SRV: { description: 'Service location', hint: 'Target hostname of the service' },
      URI: { description: 'URI reference', hint: 'URI in quotation marks' },
      HTTPS: { description: 'HTTPS service', hint: 'Priority Target Params (SVCB for HTTPS)' },
      SVCB: { description: 'Service Binding', hint: 'Priority Target Params' },
      NAPTR: { description: 'Naming Authority', hint: 'Order Pref Flags Service Regexp Replacement' },
      AFSDB: { description: 'AFS database', hint: 'Subtype Hostname' },
      HINFO: { description: 'Host info', hint: 'CPU OS (in quotation marks)' },
      IPSECKEY: { description: 'IPsec key', hint: 'Precedence GatewayType Algorithm Gateway PublicKey' },
      LOC: { description: 'Geolocation', hint: 'Lat Long Alt (RFC 1876 format)' },
      URL: { description: 'URL redirect', hint: 'Target URL for redirect' },
      categories: {
        common: 'Common',
        mail: 'Email',
        security: 'Security',
        advanced: 'Advanced'
      }
    },
    presets: {
      googleWorkspace: {
        name: 'Google Workspace Email',
        description: 'Sets up email via Google Workspace (5 MX records)'
      },
      microsoft365: {
        name: 'Microsoft 365 Email',
        description: 'Sets up email via Microsoft 365'
      },
      spfGoogle: {
        name: 'SPF for Google',
        description: 'SPF record for Google Workspace'
      },
      spfMicrosoft: {
        name: 'SPF for Microsoft',
        description: 'SPF record for Microsoft 365'
      },
      dmarc: {
        name: 'DMARC record',
        description: 'Basic DMARC protection'
      },
      webmail: {
        name: 'Enable Webmail',
        description: 'CNAME for mail.domain.tld'
      }
    }
  },
  subdomains: {
    title: 'Subdomains',
    subtitle: 'Manage subdomains for {domain}',
    subtitleShort: 'Manage your subdomains',
    add: 'Add subdomain',
    bulkActions: 'Bulk actions',
    loading: 'Loading subdomains...',
    noSubdomains: 'No subdomains available',
    noSubdomainsText: 'Add subdomains to get started.',
    name: 'Name',
    target: 'Target',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    addSubdomain: 'Add subdomain',
    editSubdomain: 'Edit subdomain',
    subdomainName: 'Subdomain',
    subdomainTarget: 'Target',
    namePlaceholder: 'e.g. blog',
    targetPlaceholder: 'e.g. /var/www/blog',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    subdomainAdded: 'Subdomain added',
    subdomainUpdated: 'Subdomain updated',
    subdomainDeleted: 'Subdomain has been deleted',
    bulkSelectAll: 'Select all',
    bulkDeselectAll: 'Deselect all',
    bulkDelete: 'Delete selected',
    bulkSelected: '{count} selected'
  },
  transferOut: {
    title: 'Transfer-Out Requests',
    subtitle: 'Manage outgoing domain transfers',
    loading: 'Loading transfer requests...',
    noTransfers: 'No transfer requests',
    noTransfersText: 'Requests will appear here when someone attempts to transfer one of your domains.',
    searchPlaceholder: 'Search domain...',
    detectedAt: 'Detected on',
    gainingRegistrar: 'New registrar',
    unknownRegistrar: 'Unknown',
    expiresAt: 'Expires on',
    stats: {
      total: 'Total',
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired'
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired',
      completed: 'Completed'
    },
    filter: {
      all: 'All',
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired'
    },
    modal: {
      title: 'Transfer-Out Request',
      warning: 'Warning',
      warningText: 'If you did NOT initiate this transfer, reject it immediately!',
      approve: 'Approve transfer',
      deny: 'Reject transfer',
      close: 'Close',
      otpTitle: 'Verification required',
      totpDescription: 'Enter the code from your authenticator app.',
      emailOtpDescription: 'A verification code has been sent to your email.',
      otpPlaceholder: '6-digit code',
      verifyAndApprove: 'Confirm & Approve',
      invalidOtp: 'Invalid code. Please try again.',
      approveSuccess: 'Transfer has been approved',
      denySuccess: 'Transfer has been rejected',
      alreadyProcessed: 'This request has already been processed.',
      notFound: 'Transfer request not found.',
      approvalLocked: 'After five wrong codes this request can no longer be approved. It can still complete automatically at the deadline, so reject it if you did not start this transfer.',
      approveNotAllowed: 'Only the account holder can approve this transfer.',
      denyNotAllowed: 'Your role in this team does not allow rejecting transfers.',
      newCodeSent: 'That code has expired or was already used. We have emailed you a new one.',
      mailUnavailable: 'We could not send the verification code. Please try again later.',
      tooManyAttempts: 'Too many attempts. Please wait a few minutes and try again.'
    },
    denyConfirm: {
      title: 'Reject transfer',
      message: 'Are you sure you want to reject this transfer?',
      confirm: 'Yes, reject',
      cancel: 'Cancel'
    }
  },
  internalTransfer: {
    title: 'Internal Transfer Requests',
    subtitle: 'Manage internal domain transfer requests',
    loading: 'Loading transfer requests...',
    noTransfers: 'No transfer requests',
    noTransfersText: 'Requests will appear here when someone wants to transfer one of your domains internally.',
    searchPlaceholder: 'Search domain...',
    requestedAt: 'Requested on',
    buyer: 'Buyer',
    unknownBuyer: 'Unknown',
    expiresAt: 'Expires on',
    stats: {
      total: 'Total',
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired'
    },
    status: {
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired'
    },
    filter: {
      all: 'All',
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied',
      expired: 'Expired'
    },
    modal: {
      title: 'Internal Transfer Request',
      warning: 'Warning',
      warningText: 'Another user wants to take over this domain. If you did NOT expect this transfer, reject it!',
      approve: 'Approve transfer',
      deny: 'Reject transfer',
      close: 'Close',
      otpTitle: 'Verification required',
      totpDescription: 'Enter the code from your authenticator app.',
      emailOtpDescription: 'A verification code has been sent to your email.',
      otpPlaceholder: '6-digit code',
      verifyAndApprove: 'Confirm & Approve',
      invalidOtp: 'Invalid code. Please try again.',
      approveSuccess: 'Transfer has been approved',
      denySuccess: 'Transfer has been rejected',
      alreadyProcessed: 'This request has already been processed.',
      notFound: 'Transfer request not found.',
      approvalLocked: 'After five wrong codes this request can no longer be approved. The domain stays with you.',
      approveNotAllowed: 'Only the account holder can approve this transfer.',
      denyNotAllowed: 'Your role in this team does not allow rejecting transfers.',
      newCodeSent: 'That code has expired or was already used. We have emailed you a new one.',
      mailUnavailable: 'We could not send the verification code. Please try again later.',
      tooManyAttempts: 'Too many attempts. Please wait a few minutes and try again.'
    },
    denyConfirm: {
      title: 'Reject transfer',
      message: 'Are you sure you want to reject this transfer?',
      confirm: 'Yes, reject',
      cancel: 'Cancel'
    }
  },
  contactManagement: {
    title: 'Contacts',
    subtitle: 'Manage your domain contacts',
    add: 'Add contact',
    loading: 'Loading contacts...',
    noContacts: 'No contacts available',
    noContactsText: 'Add contacts to get started.',
    name: 'Name',
    emailColumn: 'Email',
    type: 'Type',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    person: 'Person',
    organization: 'Organisation',
    addContact: 'Create new contact',
    editContact: 'Edit contact',
    addSubtitle: 'Fill in all required fields',
    editSubtitle: 'Modify the contact details',
    contactType: 'Type',
    selectType: 'Select type',
    firstName: 'First name',
    lastName: 'Last name',
    organizationName: 'Organisation name',
    email: 'Email',
    phone: 'Phone',
    street: 'Street',
    city: 'City',
    postcode: 'Postcode',
    state: 'County/State',
    selectState: 'Select county/state',
    country: 'Country',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    saving: 'Saving...',
    contactAdded: 'Contact added',
    contactUpdated: 'Contact updated',
    updateError: 'Contact could not be updated',
    createError: 'Contact could not be created',
    limitReached: 'An account can hold at most 25 contacts. Remove one you no longer need first.',
    defaultContactFollowsAccount: 'This contact follows your account details. Change them in your account settings.',
    deleteTitle: 'Delete contact?',
    deleteQuestion: 'Are you sure you want to permanently delete this contact?',
    deleteWarning: 'This action cannot be undone. Domains using this contact will need to be updated.',
    deleteConfirm: 'Delete',
    deleting: 'Deleting...',
    contactDeleted: 'Contact deleted',
    contactDeletedMessage: 'The contact has been successfully deleted.',
    deleteError: 'Contact could not be deleted',
    deleteNotPossible: 'Deletion not possible',
    linkedToDomain: 'This contact is linked to a domain and cannot be deleted.'
  },
  register: {
    title: 'Register domain',
    subtitle: 'Find and register your desired domain',
    search: 'Search domain',
    searchPlaceholder: 'Enter your desired domain...',
    searching: 'Searching...',
    checkAvailability: 'Check availability',
    available: 'Available',
    unavailable: 'Unavailable',
    addToCart: 'Add to cart',
    inCart: 'In cart',
    perYear: '/year',
    renewal: 'Renewal',
    resultsTitle: 'Search results',
    resultsSubtitle: 'Found domains',
    noResults: 'No domains found',
    noResultsText: 'Try a different search query.',

    singlePlaceholder: 'Enter desired domain (e.g. example or example.co.uk)',
    multiPlaceholder: 'Enter domains (one per line)\ne.g. example.co.uk\nexample.com:AUTHCODE',
    multiHint: 'Supports: line break, comma, semicolon · Max. {max}',
    pressEnterHint: 'Press Enter to check',
    multicheck: 'Multi-check',
    uploadFile: 'Upload file',
    allTldsBtn: 'All TLDs',
    format: 'Format',
    clear: 'Clear',
    checkDomain: 'Check domain',
    statsDetected: 'Detected: {count}',
    statsInvalid: 'Invalid: {count}',
    statsDuplicates: 'Duplicates removed: {count}',
    statsLimitReached: 'Limit reached ({max})',

    noResultsTitle: 'No results',
    noResultsDescription: 'No domains could be found for this search.',
    freeOnlyFilter: 'Only available domains',
    tldFilterLabel: 'Extension',
    allTlds: 'All extensions',
    sortLabel: 'Sort',
    sortRelevance: 'Relevance',
    sortPriceAsc: 'Price ascending',
    sortPriceDesc: 'Price descending',
    sortTld: 'Extension A–Z',
    resultsCount: '{count} results · {selected} selected',
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
    registerSelected: 'Register {count}',
    requestedDomainHeader: 'Your desired domain',
    creativeSuggestionsHeader: 'Creative alternatives',
    popularTldsHeader: 'Popular extensions',
    additionalFreeExtensionsHeader: 'Additional available extensions',
    searchMoreFreeExtensions: 'Search for more available extensions',
    allTldsChecked: 'All {count} TLDs have been checked',
    pricesIncludeTax: 'all prices incl. VAT',
    pricesExcludeTax: 'all prices excl. VAT',

    statusUsed: 'Taken',
    statusFree: 'Available',
    statusInvalid: 'Invalid',
    statusUnsupported: 'Not supported',
    statusProgress: 'Taken',
    statusError: 'Error',
    statusInternal: 'Taken',
    statusYours: 'Taken',
    promo: 'Promo',
    premium: 'Premium',
    copyDomain: 'Copy domain',
    authCodePlaceholder: 'Auth code',
    authCodeOptionalHint: 'No auth code needed – approval via email to admin contact (up to 15 days)',
    registerAction: 'Register',
    transferAction: 'Transfer',
    alreadyInCart: 'Already in cart',
    alreadyInCartMessage: '{domain} is already in the cart',
    addedToCart: 'Added to cart',
    addedToCartMessage: '{domain} has been added to the cart',
    goToCart: 'Go to cart',
    bulkAddedTitle: 'Domains added',
    bulkAddedMessage: '{count} domain(s) have been added to the cart'
  }
} as const

export const delegation = {
  title: 'Delegated Access',
  subtitle: 'Grant other users access to this domain',
  addDelegate: 'Grant Access',
  empty: 'No delegated users',
  emptyDescription: 'Grant other users access to manage DNS or email for this domain.',
  transferPendingHint: 'While the transfer is pending, delegates can only be removed.',
  columns: {
    email: 'Email',
    permissions: 'Permissions',
    granted: 'Granted',
    expires: 'Expires',
    actions: 'Actions'
  },
  types: {
    account: 'Account',
    mailbox: 'Mailbox'
  },
  never: 'Never',
  edit: 'Edit',
  delete: 'Remove',
  add: {
    title: 'Grant Access',
    subtitle: 'Grant access to another user',
    email: 'Email Address',
    emailPlaceholder: "user{'@'}example.com",
    emailHint: 'The user must have an account or a mailbox on this system',
    presets: 'Presets',
    presetsHint: 'Quick selection for common permission combinations',
    permissions: 'Permissions',
    permissionsHint: 'Select the permissions the user should receive',
    expiry: 'Expiry Date',
    expiryHint: 'Leave empty for unlimited access',
    noExpiry: 'No expiry date'
  },
  editModal: {
    title: 'Edit Access',
    subtitle: 'Adjust permissions for {email}'
  },
  deleteModal: {
    title: 'Remove Access',
    message: 'Are you sure you want to remove {email}\'s access to this domain?',
    warning: 'The user will immediately lose access to all delegated features.',
    confirm: 'Remove Access'
  },
  presetLabels: {
    mailAdmin: 'Mail Administrator',
    dnsAdmin: 'DNS Administrator',
    listManager: 'Mailing List Manager'
  },
  permissionLabels: {
    'dns:read': 'Read DNS',
    'dns:write': 'Manage DNS',
    'dyndns:read': 'Read DynDNS',
    'dyndns:write': 'Manage DynDNS',
    'mailbox:read': 'Read Mailboxes',
    'mailbox:write': 'Manage Mailboxes',
    'mailbox:delete': 'Delete Mailboxes',
    'mailinglist:read': 'Read Mailing Lists',
    'mailinglist:write': 'Manage Mailing Lists',
    'alias:read': 'View Aliases',
    'alias:write': 'Manage Aliases',
    'alias:delete': 'Delete Aliases'
  },
  permissionGroups: {
    dns: 'DNS',
    mail: 'Email',
    lists: 'Mailing Lists',
    aliases: 'Aliases'
  },
  notifications: {
    added: 'Access Granted',
    addedMessage: '{email} now has access to this domain.',
    updated: 'Access Updated',
    updatedMessage: 'Permissions have been updated.',
    deleted: 'Access Removed',
    deletedMessage: '{email} no longer has access to this domain.',
    accountNotFound: 'No account or mailbox found with this email address.',
    alreadyDelegate: 'This user already has access to this domain.',
    mailboxPermissionsOnly: 'Mailbox users can only receive email permissions (mailboxes and mailing lists).'
  }
} as const

export const domain = {
  dyndns: {
    title: 'DynDNS',
    subtitle: 'Dynamic DNS updates for changing IP addresses',
    empty: 'No DynDNS tokens available',
    emptyDescription: 'Create a token to enable dynamic IP updates from your router.',
    createToken: 'Create token',
    columns: {
      hostname: 'Hostname',
      type: 'Type',
      currentIp: 'Current IP',
      lastUpdate: 'Last update',
      status: 'Status',
      actions: 'Actions'
    },
    status: {
      rateLimited: 'Rate limited',
      rateLimitedUntil: 'Available {time}',
      invalidIp: 'Invalid IP',
      dnsError: 'DNS error'
    },
    recordTypes: {
      A: 'IPv4 only',
      AAAA: 'IPv6 only',
      BOTH: 'IPv4 + IPv6'
    },
    create: {
      title: 'Create DynDNS token',
      hostname: 'Hostname',
      hostnameHint: "Empty or {'@'} for root domain",
      recordType: 'Record type',
      description: 'Description',
      descriptionHint: 'e.g. "Home router"',
      wildcardWarning: 'Wildcard entries (*.domain.tld) will override all subdomains without explicit DNS records.'
    },
    created: {
      title: 'Token created',
      titleRotated: 'Token renewed',
      warning: 'Save this token now - it will not be shown again!',
      warningRotated: 'The old token is now invalid.',
      copyToken: 'Copy token',
      copied: 'Copied!',
      configTitle: 'Client configuration',
      clients: {
        fritzbox: 'Fritz!Box',
        ddclient: 'ddclient',
        curl: 'curl',
        openwrt: 'OpenWrt',
        synology: 'Synology'
      },
      copyConfig: 'Copy configuration',
      copyValue: 'Copy value',
      fields: {
        dyndnsProvider: 'DynDNS Provider',
        ddnsProvider: 'DDNS Provider',
        custom: 'Custom',
        updateUrl: 'Update URL',
        serviceUrl: 'Service URL',
        domainName: 'Domain name',
        hostname: 'Hostname',
        username: 'Username',
        password: 'Password',
        passwordSynology: 'Password'
      }
    },
    rotate: {
      tooltip: 'Renew token',
      confirm: 'Do you really want to renew the token? The old token will become invalid immediately.'
    },
    delete: {
      title: 'Delete token',
      message: 'Do you really want to delete this DynDNS token?',
      warning: 'The associated DNS records will also be removed.',
      confirm: 'Delete token'
    },
    notifications: {
      createSuccess: 'DynDNS token created',
      rotateSuccess: 'DynDNS token renewed',
      deleteSuccess: 'DynDNS token deleted',
      maxTokensReached:
        'Maximum number of DynDNS tokens reached ({limit}). Delete a token you no longer need to create a new one.',
      externalNameservers:
        'This domain uses external nameservers. DynDNS only works with our nameservers.',
      hostnameHasRecords:
        'A/AAAA records already exist for this hostname. Delete them first.'
    },
    neverUpdated: 'Never',
    noIp: 'No IP',
    wildcardTooltip: 'Affects all subdomains without explicit DNS records'
  }
} as const
